
import { useState, useEffect, useCallback } from 'react';
import { getSocialProfiles, deleteSocialProfile } from '@/lib/supabase/social';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase/client';
import { SocialProfile } from '../types';

export const useProfiles = (isSingleSelectionMode: boolean) => {
  // Define all state hooks first
  const [profiles, setProfiles] = useState<SocialProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);
  const [singleSelectedProfile, setSingleSelectedProfile] = useState<string>('');
  const { toast } = useToast();

  // Memoize callback functions with useCallback
  const handleRemoveProfile = useCallback(async (id: string) => {
    try {
      const result = await deleteSocialProfile(id);
      
      if (!result.success) {
        throw new Error(`Erreur lors de la suppression du profil: ${result.error}`);
      }
      
      setProfiles(prevProfiles => prevProfiles.filter(profile => profile.id !== id));
      toast({
        title: "Profil supprimé",
        description: "Le profil a été supprimé avec succès",
      });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: "Erreur",
        description: error instanceof Error ? error.message : "Erreur lors de la suppression du profil",
        variant: "destructive",
      });
    }
  }, [toast]);

  const toggleProfileSelection = useCallback((id: string) => {
    if (isSingleSelectionMode) {
      setSingleSelectedProfile(id);
    } else {
      setSelectedProfiles(prev => 
        prev.includes(id) 
          ? prev.filter(profileId => profileId !== id) 
          : [...prev, id]
      );
    }
  }, [isSingleSelectionMode]);

  // Use effect hook
  useEffect(() => {
    const fetchProfiles = async () => {
      setIsLoading(true);
      try {
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('id')
          .single();
        
        if (orgError) {
          throw new Error(`Erreur lors de la récupération de l'organisation: ${orgError.message}`);
        }
        
        if (!orgData?.id) {
          throw new Error("Aucune organisation trouvée pour cet utilisateur");
        }

        const result = await getSocialProfiles(orgData.id);
        
        if (!result.success) {
          throw new Error(`Erreur lors de la récupération des profils sociaux: ${result.error}`);
        }
        
        if (!result.data || result.data.length === 0) {
          setProfiles([]);
          setSelectedProfiles([]);
          setSingleSelectedProfile('');
        } else {
          // Convert from database SocialProfile to component SocialProfile
          const formattedProfiles: SocialProfile[] = result.data.map(profile => ({
            id: profile.id,
            name: profile.name,
            platform: profile.platform as any,
            username: profile.profile_url || '',
            avatarUrl: '',
            isConnected: true,
            stats: profile.stats,
            youtube_channel_id: profile.youtube_channel_id,
            youtube_access_token: profile.youtube_access_token,
            youtube_refresh_token: profile.youtube_refresh_token,
            youtube_token_expiry: profile.youtube_token_expiry
          }));
          
          setProfiles(formattedProfiles);
          
          if (formattedProfiles.length > 0) {
            if (isSingleSelectionMode) {
              setSingleSelectedProfile(formattedProfiles[0].id);
            } else {
              setSelectedProfiles([formattedProfiles[0].id]);
            }
          }
        }
      } catch (error) {
        console.error('Erreur:', error);
        toast({
          title: "Erreur",
          description: error instanceof Error ? error.message : "Erreur lors du chargement des profils sociaux",
          variant: "destructive",
        });
        
        setProfiles([]);
        setSelectedProfiles([]);
        setSingleSelectedProfile('');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfiles();
  }, [toast, isSingleSelectionMode]);

  return {
    profiles,
    isLoading,
    selectedProfiles,
    singleSelectedProfile,
    setSingleSelectedProfile,
    setSelectedProfiles,
    handleRemoveProfile,
    toggleProfileSelection
  };
};
